/**
 * CustomColorPicker Component
 * 
 * Provides a dialog interface for selecting preset themes or creating custom themes.
 * Features tab-based interface for dark/light mode customization, preset theme grid,
 * custom color inputs, and real-time preview.
 */

import React, { useState, useEffect } from 'react';
import { themePresets } from '@/config/themePresets';
import { isValidHex, applyGlobalThemeColors } from '@/utils/colorUtils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';

const CustomColorPicker = ({ open, onOpenChange }) => {
  // Active tab state (dark or light mode)
  const [activeTab, setActiveTab] = useState('dark');
  const [selectedPreset, setSelectedPreset] = useState('emerald-glow');

  // Temporary color state for real-time preview
  const [tempColors, setTempColors] = useState({
    light: {
      primary: '#16a34a',
      secondary: '#f5f5f5',
      tertiary: '#1f2937',
    },
    dark: {
      primary: '#22c55e',
      secondary: '#0a0a0a',
      tertiary: '#ffffff',
    },
  });

  // Validation errors for color inputs
  const [validationErrors, setValidationErrors] = useState({
    light: {},
    dark: {},
  });

  /**
   * Handles color input change with validation
   */
  const handleColorChange = (mode, colorKey, value) => {
    // Update temp colors
    setTempColors((prev) => ({
      ...prev,
      [mode]: {
        ...prev[mode],
        [colorKey]: value,
      },
    }));

    // Validate the color
    if (!isValidHex(value)) {
      setValidationErrors((prev) => ({
        ...prev,
        [mode]: {
          ...prev[mode],
          [colorKey]: 'Invalid hex color format (e.g., #16a34a)',
        },
      }));
    } else {
      // Clear error if valid
      setValidationErrors((prev) => ({
        ...prev,
        [mode]: {
          ...prev[mode],
          [colorKey]: undefined,
        },
      }));
    }
  };

  /**
   * Handles preset theme selection
   */
  const handlePresetSelect = (preset) => {
    setSelectedPreset(preset.id);
    setTempColors({
      light: preset.light,
      dark: preset.dark,
    });
    // Clear validation errors
    setValidationErrors({
      light: {},
      dark: {},
    });
  };

  /**
   * Checks if all colors are valid
   */
  const areAllColorsValid = () => {
    const lightValid =
      isValidHex(tempColors.light.primary) &&
      isValidHex(tempColors.light.secondary) &&
      isValidHex(tempColors.light.tertiary);
    const darkValid =
      isValidHex(tempColors.dark.primary) &&
      isValidHex(tempColors.dark.secondary) &&
      isValidHex(tempColors.dark.tertiary);
    return lightValid && darkValid;
  };

  /**
   * Applies theme colors to CSS variables
   */
  const applyThemeColors = (colors) => {
    try {
      // Use the global theme application utility
      applyGlobalThemeColors(colors);
      
      // Store theme in localStorage
      localStorage.setItem('customTheme', JSON.stringify({
        light: tempColors.light,
        dark: tempColors.dark,
        selectedPreset
      }));
    } catch (error) {
      console.error('Failed to apply theme colors:', error);
    }
  };

  /**
   * Handles applying the custom theme
   */
  const handleApply = () => {
    if (areAllColorsValid()) {
      // Determine which colors to apply based on current theme mode
      const currentMode = document.documentElement.classList.contains('dark') ? 'dark' : 'light';
      applyThemeColors(tempColors[currentMode]);
      onOpenChange(false);
    }
  };

  /**
   * Handles resetting to default theme
   */
  const handleReset = () => {
    const defaultPreset = themePresets[0];
    setSelectedPreset(defaultPreset.id);
    setTempColors({
      light: defaultPreset.light,
      dark: defaultPreset.dark,
    });
    setValidationErrors({
      light: {},
      dark: {},
    });
    
    // Apply default theme
    const currentMode = document.documentElement.classList.contains('dark') ? 'dark' : 'light';
    applyThemeColors(defaultPreset[currentMode]);
    
    // Remove custom theme from localStorage
    localStorage.removeItem('customTheme');
    onOpenChange(false);
  };

  // Initialize tempColors from localStorage or default when dialog opens
  useEffect(() => {
    if (open) {
      const savedTheme = localStorage.getItem('customTheme');
      if (savedTheme) {
        try {
          const parsed = JSON.parse(savedTheme);
          setTempColors(parsed);
          setSelectedPreset(parsed.selectedPreset || 'emerald-glow');
        } catch (error) {
          console.error('Failed to parse saved theme:', error);
          const defaultPreset = themePresets[0];
          setTempColors({
            light: defaultPreset.light,
            dark: defaultPreset.dark,
          });
          setSelectedPreset(defaultPreset.id);
        }
      } else {
        const defaultPreset = themePresets[0];
        setTempColors({
          light: defaultPreset.light,
          dark: defaultPreset.dark,
        });
        setSelectedPreset(defaultPreset.id);
      }
    }
  }, [open]);

  /**
   * Effect for real-time preview - applies tempColors to CSS variables
   */
  useEffect(() => {
    if (!open) return;

    // Determine which mode colors to preview
    const currentMode = document.documentElement.classList.contains('dark') ? 'dark' : 'light';
    const colorsToPreview = tempColors[currentMode];

    // Only apply if all colors are valid
    if (
      isValidHex(colorsToPreview.primary) &&
      isValidHex(colorsToPreview.secondary) &&
      isValidHex(colorsToPreview.tertiary)
    ) {
      applyThemeColors(colorsToPreview);
    }
  }, [tempColors, open]);

  // Listen for theme mode changes to update preview
  useEffect(() => {
    if (!open) return;

    const handleThemeChange = () => {
      const currentMode = document.documentElement.classList.contains('dark') ? 'dark' : 'light';
      const colorsToPreview = tempColors[currentMode];
      
      if (
        isValidHex(colorsToPreview.primary) &&
        isValidHex(colorsToPreview.secondary) &&
        isValidHex(colorsToPreview.tertiary)
      ) {
        applyThemeColors(colorsToPreview);
      }
    };

    // Use MutationObserver to watch for class changes on document element
    const observer = new MutationObserver(handleThemeChange);
    observer.observe(document.documentElement, { 
      attributes: true, 
      attributeFilter: ['class'] 
    });

    return () => observer.disconnect();
  }, [tempColors, open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            Customize Theme Colors
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onOpenChange(false)}
              className="h-6 w-6 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </DialogTitle>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="dark">Dark Mode</TabsTrigger>
            <TabsTrigger value="light">Light Mode</TabsTrigger>
          </TabsList>

          <TabsContent value="dark" className="space-y-4">
            {/* Preset Themes Section */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium">Preset Themes</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {themePresets.map((preset) => {
                  const isSelected = selectedPreset === preset.id;
                  return (
                    <button
                      key={preset.id}
                      onClick={() => handlePresetSelect(preset)}
                      className={`
                        relative p-3 rounded-lg border-2 transition-all
                        hover:border-primary/50
                        ${isSelected ? 'border-primary ring-2 ring-primary/20' : 'border-border'}
                      `}
                    >
                      <div className="space-y-2">
                        <div className="flex gap-1 h-6">
                          <div
                            className="flex-1 rounded"
                            style={{ backgroundColor: preset.dark.primary }}
                          />
                          <div
                            className="flex-1 rounded"
                            style={{ backgroundColor: preset.dark.secondary }}
                          />
                          <div
                            className="flex-1 rounded"
                            style={{ backgroundColor: preset.dark.tertiary }}
                          />
                        </div>
                        <p className="text-xs font-medium truncate">{preset.name}</p>
                      </div>
                      {isSelected && (
                        <div className="absolute top-1 right-1 text-primary">
                          <span className="text-xs">✓</span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Custom Colors Section */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium">Custom Colors</h3>
              
              {/* Primary Color */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">
                  Primary Color
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={tempColors.dark.primary}
                    onChange={(e) => handleColorChange('dark', 'primary', e.target.value)}
                    className="h-10 w-16 rounded border cursor-pointer"
                  />
                  <input
                    type="text"
                    value={tempColors.dark.primary}
                    onChange={(e) => handleColorChange('dark', 'primary', e.target.value)}
                    placeholder="#22c55e"
                    className="flex-1 h-10 px-3 rounded border bg-background text-sm"
                  />
                </div>
                {validationErrors.dark.primary && (
                  <p className="text-xs text-destructive">{validationErrors.dark.primary}</p>
                )}
              </div>

              {/* Secondary Color */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">
                  Secondary Color
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={tempColors.dark.secondary}
                    onChange={(e) => handleColorChange('dark', 'secondary', e.target.value)}
                    className="h-10 w-16 rounded border cursor-pointer"
                  />
                  <input
                    type="text"
                    value={tempColors.dark.secondary}
                    onChange={(e) => handleColorChange('dark', 'secondary', e.target.value)}
                    placeholder="#0a0a0a"
                    className="flex-1 h-10 px-3 rounded border bg-background text-sm"
                  />
                </div>
                {validationErrors.dark.secondary && (
                  <p className="text-xs text-destructive">{validationErrors.dark.secondary}</p>
                )}
              </div>

              {/* Tertiary Color */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">
                  Tertiary Color
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={tempColors.dark.tertiary}
                    onChange={(e) => handleColorChange('dark', 'tertiary', e.target.value)}
                    className="h-10 w-16 rounded border cursor-pointer"
                  />
                  <input
                    type="text"
                    value={tempColors.dark.tertiary}
                    onChange={(e) => handleColorChange('dark', 'tertiary', e.target.value)}
                    placeholder="#ffffff"
                    className="flex-1 h-10 px-3 rounded border bg-background text-sm"
                  />
                </div>
                {validationErrors.dark.tertiary && (
                  <p className="text-xs text-destructive">{validationErrors.dark.tertiary}</p>
                )}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="light" className="space-y-4">
            {/* Preset Themes Section */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium">Preset Themes</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {themePresets.map((preset) => {
                  const isSelected = selectedPreset === preset.id;
                  return (
                    <button
                      key={preset.id}
                      onClick={() => handlePresetSelect(preset)}
                      className={`
                        relative p-3 rounded-lg border-2 transition-all
                        hover:border-primary/50
                        ${isSelected ? 'border-primary ring-2 ring-primary/20' : 'border-border'}
                      `}
                    >
                      <div className="space-y-2">
                        <div className="flex gap-1 h-6">
                          <div
                            className="flex-1 rounded"
                            style={{ backgroundColor: preset.light.primary }}
                          />
                          <div
                            className="flex-1 rounded"
                            style={{ backgroundColor: preset.light.secondary }}
                          />
                          <div
                            className="flex-1 rounded"
                            style={{ backgroundColor: preset.light.tertiary }}
                          />
                        </div>
                        <p className="text-xs font-medium truncate">{preset.name}</p>
                      </div>
                      {isSelected && (
                        <div className="absolute top-1 right-1 text-primary">
                          <span className="text-xs">✓</span>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Custom Colors Section */}
            <div className="space-y-3">
              <h3 className="text-sm font-medium">Custom Colors</h3>
              
              {/* Primary Color */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">
                  Primary Color
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={tempColors.light.primary}
                    onChange={(e) => handleColorChange('light', 'primary', e.target.value)}
                    className="h-10 w-16 rounded border cursor-pointer"
                  />
                  <input
                    type="text"
                    value={tempColors.light.primary}
                    onChange={(e) => handleColorChange('light', 'primary', e.target.value)}
                    placeholder="#16a34a"
                    className="flex-1 h-10 px-3 rounded border bg-background text-sm"
                  />
                </div>
                {validationErrors.light.primary && (
                  <p className="text-xs text-destructive">{validationErrors.light.primary}</p>
                )}
              </div>

              {/* Secondary Color */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">
                  Secondary Color
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={tempColors.light.secondary}
                    onChange={(e) => handleColorChange('light', 'secondary', e.target.value)}
                    className="h-10 w-16 rounded border cursor-pointer"
                  />
                  <input
                    type="text"
                    value={tempColors.light.secondary}
                    onChange={(e) => handleColorChange('light', 'secondary', e.target.value)}
                    placeholder="#f5f5f5"
                    className="flex-1 h-10 px-3 rounded border bg-background text-sm"
                  />
                </div>
                {validationErrors.light.secondary && (
                  <p className="text-xs text-destructive">{validationErrors.light.secondary}</p>
                )}
              </div>

              {/* Tertiary Color */}
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">
                  Tertiary Color
                </label>
                <div className="flex gap-2">
                  <input
                    type="color"
                    value={tempColors.light.tertiary}
                    onChange={(e) => handleColorChange('light', 'tertiary', e.target.value)}
                    className="h-10 w-16 rounded border cursor-pointer"
                  />
                  <input
                    type="text"
                    value={tempColors.light.tertiary}
                    onChange={(e) => handleColorChange('light', 'tertiary', e.target.value)}
                    placeholder="#1f2937"
                    className="flex-1 h-10 px-3 rounded border bg-background text-sm"
                  />
                </div>
                {validationErrors.light.tertiary && (
                  <p className="text-xs text-destructive">{validationErrors.light.tertiary}</p>
                )}
              </div>
            </div>
          </TabsContent>
        </Tabs>

        <DialogFooter>
          <Button variant="outline" onClick={handleReset}>
            Reset to Default
          </Button>
          <Button 
            onClick={handleApply}
            disabled={!areAllColorsValid()}
          >
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CustomColorPicker;